import { APP_INITIALIZER, Injector, NgModule } from "@angular/core";
import { BrowserModule } from "@angular/platform-browser";
import { Router, RouteReuseStrategy } from "@angular/router";

import {
  IonicModule,
  IonicRouteStrategy,
  NavParams,
  Platform,
} from "@ionic/angular";

import { AppRoutingModule } from "./app-routing.module";
import { AppComponent } from "./app.component";

import { HttpClientModule } from "@angular/common/http";

import { AuthenticationService } from "./services/authentication/authentication.service";
import { GlobalErrorHandlerService } from "./services/global-errors/global-error-handler.service";
import { UserSessionService } from "./services/user-session/user-session.service";
import { LogoutService } from "./services/logout/logout.service";
import { ServicesSharedModule } from "./services/services-shared.module";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { ConfigService } from "./services/config/config.service";
import { ComponentsModule } from "./components/components.module";
import { pagesModule } from "./pages/pages.module";
import { IonicStorageModule } from "@ionic/storage-angular";
import { TouchID } from "@ionic-native/touch-id/ngx";
import { Device } from "@ionic-native/device/ngx";
import { CommonModule, DatePipe } from "@angular/common";
import { AuthGuardService } from "./services/auth-guard/auth-guard.service";
import { ScreenOrientation } from "@ionic-native/screen-orientation/ngx";
import { InAppBrowser } from "@ionic-native/in-app-browser/ngx";
import { NotificationApiService } from "./services/notification-api/notification-api.service";
import { AppstorageTsService } from "./services/appstorage/appstorage.ts.service";
import { NgIdleModule } from '@ng-idle/core';
import { TabLandingPageModule } from "./tabs/tab-landing/tab-landing.module";
import { MarketingService } from "./services/marketing/marketing.service";
import { customTabAnimation } from "./animation/animation";
import { FileOpener } from "@ionic-native/file-opener/ngx";
import { LoginComponent } from "./pages/login/login.component";
import { SignupOptionsComponent } from "./pages/signup-options/signup-options.component";
import { AccountInformationComponent } from "./pages/information-and-security/account-information/account-information.component";
import { AccountBalanceComponent } from "./pages/account-balance/account-balance.component";
import { ActiveBonusesComponent } from "./pages/rewards/bonuses/active-bonuses/active-bonuses.component";
import { BoydRewardsConnectComponent } from "./pages/boyd-rewards/boyd-rewards-connect/boyd-rewards-connect.component";
import { BoydMyBalanceComponent } from "./pages/boyd-rewards/boyd-my-balance/boyd-my-balance.component";
import { BonusDetailsComponent } from "./pages/rewards/bonuses/bonus-details/bonus-details.component"; 
import { AppUpdateComponent } from "./pages/app-update/app-update.component";
import { AppRestrictedComponent } from "./pages/app-restricted/app-restricted.component";
import { BonusOffersComponent } from "./pages/rewards/bonuses/bonus-offers/bonus-offers.component";
import { ContentPlaceholderComponent } from "./pages/content-placeholder/content-placeholder.component";
import { DailySlotComponent } from "./pages/daily-slot/daily-slot.component";
import { DepositCageComponent } from "./pages/cashier/deposit/deposit-cage/deposit-cage.component";
import { DepositCreditcardComponent } from "./pages/cashier/deposit/deposit-creditcard/deposit-creditcard.component";
import { DepositMainComponent } from "./pages/cashier/deposit/deposit-main/deposit-main.component";
import { DepositPaynearmeComponent } from "./pages/cashier/deposit/deposit-paynearme/deposit-paynearme.component";
import { DepositPaypalComponent } from "./pages/cashier/deposit/deposit-paypal/deposit-paypal.component";
import { DepositPlayplusComponent } from "./pages/cashier/deposit/deposit-playplus/deposit-playplus.component";
import { DepositPlayplusActionComponent } from "./pages/cashier/deposit/deposit-playplus/deposit-playplus-action.component";
import { DepositPaywithmybankComponent } from "./pages/cashier/deposit/deposit-paywithmybank/deposit-paywithmybank.component";
import { DepositVenmoComponent } from "./pages/cashier/deposit/deposit-venmo/deposit-venmo.component";
import { DepositVipComponent } from "./pages/cashier/deposit/deposit-vip/deposit-vip.component";
import { DepositSuccessFailureComponent } from "./pages/cashier/deposit/deposit-success-failure/deposit-success-failure.component";
import { DepositThresholdComponent } from "./pages/cashier/deposit/deposit-threshold/deposit-threshold.component";
import { DepositWireComponent } from "./pages/cashier/deposit/deposit-wire/deposit-wire.component";
import { GeoComplyComponent } from "./pages/geo-comply/geo-comply.component";
import { ForgotPasswordComponent } from "./pages/forgot-password/forgot-password.component";
import { MyFreeSpinComponent } from "./pages/rewards/bonuses/my-free-spin/my-free-spin.component";
import { PlayerProtectionComponent } from "./pages/responsible-gaming/player-protection/player-protection.component";
import { RedeemBonusCodeComponent } from "./pages/rewards/bonuses/redeem-bonus-code/redeem-bonus-code.component";
import { PastBonusesComponent } from "./pages/rewards/bonuses/past-bonuses/past-bonuses.component";
import { RiskFreeBetRiskComponent } from "./pages/rewards/bonuses/risk-free-bet-risk/risk-free-bet-risk.component";
import { SignupStepOneComponent } from "./pages/signup-step-one/signup-step-one.component";
import { SignupStepTwoComponent } from "./pages/signup-step-two/signup-step-two.component";
import { CloseAccountComponent } from "./pages/close-account/close-account.component";
import { PersonalInformationComponent } from "./pages/information-and-security/personal-information/personal-information.component";
import { UploadDocumentComponent } from "./pages/information-and-security/upload-document/upload-document.component";
import { ChangePasswordComponent } from "./pages/information-and-security/change-password/change-password.component";
import { SetDepositLimitsComponent } from "./pages/limits/set-deposit-limits/set-deposit-limits.component";
import { ResendPinComponent } from "./pages/information-and-security/resend-pin/resend-pin.component";
import { SecurityQuestionComponent } from "./pages/information-and-security/security-question/security-question.component";
import { PendingWithdrawalsComponent } from "./pages/cashier/overview/pending-withdrawals/pending-withdrawals.component";
import { RafOverviewComponent } from "./pages/refer-a-friend/raf-overview/raf-overview.component";
import { RafInviteEmailComponent } from "./pages/refer-a-friend/raf-invite-email/raf-invite-email.component";
import { RejectedWithdrawalsComponent } from "./pages/cashier/overview/rejected-withdrawals/rejected-withdrawals.component";
import { SetTimeLimitsComponent } from "./pages/limits/set-time-limits/set-time-limits.component";
import { SetLossLimitsComponent } from "./pages/limits/set-loss-limits/set-loss-limits.component";
import { SetSingleBetLimitsComponent } from "./pages/limits/set-single-bet-limits/set-single-bet-limits.component";
import { TaxReportsComponent } from "./pages/tax-reports/tax-reports.component";
import { TotalDepositsComponent } from "./pages/cashier/overview/total-deposits/total-deposits.component";
import { TotalWithdrawalsComponent } from "./pages/cashier/overview/total-withdrawals/total-withdrawals.component";
import { WithdrawalPaypalComponent } from "./pages/cashier/withdraw/withdrawal-paypal/withdrawal-paypal.component";
import { WithdrawalVipComponent } from "./pages/cashier/withdraw/withdrawal-vip/withdrawal-vip.component";
import { WithdrawalPrepaidComponent } from "./pages/cashier/withdraw/withdrawal-prepaid/withdrawal-prepaid.component";
import { WithdrawalCageComponent } from "./pages/cashier/withdraw/withdrawal-cage/withdrawal-cage.component";
import { WithdrawalReverseComponent } from "./pages/cashier/withdraw/withdrawal-reverse/withdrawal-reverse.component";
import { WithdrawalReverseConfirmComponent } from "./pages/cashier/withdraw/withdrawal-reverse-confirm/withdrawal-reverse-confirm.component";
import { WithdrawalPayWithMyBankComponent } from "./pages/cashier/withdraw/withdrawal-pay-with-my-bank/withdrawal-pay-with-my-bank.component";
import { TransactionListComponent } from "./components/transaction-list/transaction-list.component";
import { LimitsHistoryComponent } from "./pages/limits/limits-history/limits-history.component";
import { StrongAuthenticationComponent } from "./pages/information-and-security/strong-authentication/strong-authentication.component";
import { GameSettingsComponent } from "./pages/information-and-security/game-settings/game-settings.component";
import { WithdrawalMailComponent } from "./pages/cashier/withdraw/withdrawal-mail/withdrawal-mail.component";
import { WinLossComponent } from "./pages/cashier/win-loss/win-loss.component";
import { CoolOffComponent } from "./pages/cool-off/cool-off.component";
import { AccountSummaryComponent } from "./pages/account-summary/account-summary.component";
import { SelfExcludeComponent } from "./pages/self-exclude/self-exclude.component";
import { GameTransactionsComponent } from "./pages/game-transactions/game-transactions.component";
import { IdologyKbaComponent } from "./pages/idology-kba/idology-kba.component";
import { InvalidKycComponent } from "./pages/invalid-kyc/invalid-kyc.component";
import { SignupResultComponent } from "./pages/signup-result/signup-result.component";
import { UpdatePhoneNumberComponent } from "./pages/update-phone-number/update-phone-number.component";
import { GameHistoryComponent } from "./pages/game-history/game-history.component";
import { DeleteAccountComponent } from "./pages/delete-account/delete-account.component";
import { SmsLoginComponent } from "./pages/sms-login/sms-login.component";
import { ManageNotificationsComponent } from "./pages/information-and-security/manage-notifications/manage-notifications.component";
import { SpendLimitComponent } from "./pages/limits/spend-limit/spend-limit.component";
import { MyTransactionMethodsComponent } from "./pages/my-transaction-methods/my-transaction-methods.component";
import { DepositPayWithCashComponent } from "./pages/cashier/deposit/deposit-pay-with-cash/deposit-pay-with-cash.component";
import { UpdateTocComponent } from "./pages/update-toc/update-toc.component";
import { UpdatePrivacyPolicyComponent } from "./pages/update-privacy-policy/update-privacy-policy.component";
import { UpdateHouseRulesComponent } from "./pages/update-house-rules/update-house-rules.component";
import { ResetPasswordComponent } from "./pages/reset-password/reset-password.component";
import { CoolingOffComponent } from "./pages/cooling-off/cooling-off.component";
import { ResetPasswordVerifyFirstStepComponent } from "./pages/reset-password/reset-password-verify-first-step/reset-password-verify-first-step.component";
import { ResetPasswordVerifySecondStepComponent } from "./pages/reset-password/reset-password-verify-second-step/reset-password-verify-second-step.component";
import { ResetPasswordVerifyThirdStepComponent } from "./pages/reset-password/reset-password-verify-third-step/reset-password-verify-third-step.component";
import { ExternalLinkContentComponent } from "./pages/external-link-content/external-link-content.component";
import { PasswordResetComponent } from "./pages/password-reset/password-reset.component";
import { WithdrawalVenmoComponent } from "./pages/cashier/withdraw/withdrawal-venmo/withdrawal-venmo.component";
import { PlayerMigrationActionComponent } from "./pages/player-migration/player-migration-action.component";
import { LimitConfirmationComponent } from "./pages/limits/limit-confirmation/limit-confirmation.component";
import { BoydRedeemRewardsComponent } from "./pages/boyd-rewards/boyd-redeem-rewards/boyd-redeem-rewards.component";
import { CamsComponent } from "./pages/cams/cams.component";
import { IdNowComponent } from "./pages/id-now/id-now.component";
import { EnrollBoydRewardsComponent } from "./pages/boyd-rewards/enroll-boyd-rewards/enroll-boyd-rewards.component";
import { WithdrawalFastAccessComponent } from "./pages/cashier/withdraw/withdrawal-fastaccess/withdrawal-fastaccess";
import { ChangePasswordMFAComponent } from "./pages/information-and-security/change-password/change-password-mfa/change-password-mfa.component";
import { ChangePasswordMFAStepTwoComponent } from "./pages/information-and-security/change-password/change-password-mfa-step-two/change-password-mfa-step-two.component";
import { ApplePay } from "@ionic-native/apple-pay/ngx";
import { ResetPasswordMFAEmailComponent } from "./pages/reset-password/reset-password-mfa-email/reset-password-mfa-email.component";
import { W2GPopoverComponent } from "./popovers/w2g/w2g-popover.component";
import { DepositApplepayComponent } from "./pages/cashier/deposit/deposit-applepay/deposit-applepay.component";

//each domain should contain 'default_roue' propertry defined in environment.config file which code checks which specific routes to load

//These are modal component routes
//We need to add any new modal component here so, code can display url friendly name.
const COMMON_MODAL_COMPONENT_ROUTES = [
  { path: "login", component: LoginComponent },
  { path: "signupoptions", component: SignupOptionsComponent },
  { path: "accountinformation", component: AccountInformationComponent },
  { path: "accountbalance", component: AccountBalanceComponent },
  { path: "activebonuses", component: ActiveBonusesComponent },
  { path: "boydrewardsconnect", component: BoydRewardsConnectComponent },
  { path: "enrollboydrewards", component: EnrollBoydRewardsComponent },
  { path: "boydmybalance", component: BoydMyBalanceComponent },
  { path: "bonusdetails", component: BonusDetailsComponent },
  { path: "appupdate", component: AppUpdateComponent },
  { path: "apprestricted", component: AppRestrictedComponent },
  { path: "bonusoffers", component: BonusOffersComponent },
  { path: "contentplaceholder", component: ContentPlaceholderComponent },
  { path: "dailyslot", component: DailySlotComponent },
  { path: "depositcage", component: DepositCageComponent },
  { path: "depositcreditcard", component: DepositCreditcardComponent },
  { path: "depositmain", component: DepositMainComponent },
  { path: "depositpaynearme", component: DepositPaynearmeComponent },
  { path: "depositpaypal", component: DepositPaypalComponent },
  { path: "depositplayplus", component: DepositPlayplusComponent },
  { path: "depositplayplusaction", component: DepositPlayplusActionComponent },
  { path: "depositpaywithmybank", component: DepositPaywithmybankComponent },
  { path: "depositvenmo", component: DepositVenmoComponent },
  { path: "depositvip", component: DepositVipComponent },
  { path: "depositsuccessfailure", component: DepositSuccessFailureComponent },
  { path: "depositthreshold", component: DepositThresholdComponent },
  { path: "depositwire", component: DepositWireComponent },
  { path: "geocomply", component: GeoComplyComponent },
  { path: "forgotpassword", component: ForgotPasswordComponent },
  { path: "myfreespin", component: MyFreeSpinComponent },
  { path: "playerprotection", component: PlayerProtectionComponent },
  { path: "pastbonuses", component: PastBonusesComponent },
  { path: "redeembonuscode", component: RedeemBonusCodeComponent },
  { path: "riskfreebetrisk", component: RiskFreeBetRiskComponent },
  { path: "signupstepone", component: SignupStepOneComponent },
  { path: "signupsteptwo", component: SignupStepTwoComponent },
  { path: "closeaccount", component: CloseAccountComponent },
  { path: "personalinformation", component: PersonalInformationComponent },
  { path: "uploaddocument", component: UploadDocumentComponent },
  { path: "changepassword", component: ChangePasswordComponent },
  { path: "changepasswordmfa", component: ChangePasswordMFAComponent },
  { path: "changepasswordmfaemail", component: ResetPasswordMFAEmailComponent },
  { path: "changepasswordmfasteptwo", component: ChangePasswordMFAStepTwoComponent },
  { path: "setdepositlimits", component: SetDepositLimitsComponent },
  { path: "resendpin", component: ResendPinComponent },
  { path: "securityquestion", component: SecurityQuestionComponent },
  { path: "pendingwithdrawals", component: PendingWithdrawalsComponent },
  { path: "rafoverview", component: RafOverviewComponent },
  { path: "rafinviteemail", component: RafInviteEmailComponent },
  { path: "rejectedwithdrawals", component: RejectedWithdrawalsComponent },
  { path: "settimelimits", component: SetTimeLimitsComponent },
  { path: "setlosslimits", component: SetLossLimitsComponent },
  { path: "setsinglebetlimits", component: SetSingleBetLimitsComponent },
  { path: "taxreports", component: TaxReportsComponent },
  { path: "totaldeposits", component: TotalDepositsComponent },
  { path: "totalwithdrawals", component: TotalWithdrawalsComponent },
  { path: "withdrawalpaypal", component: WithdrawalPaypalComponent },
  { path: "withdrawalfastaccess", component: WithdrawalFastAccessComponent },
  { path: "withdrawalvip", component: WithdrawalVipComponent },
  { path: "withdrawalprepaid", component: WithdrawalPrepaidComponent },
  { path: "withdrawalcage", component: WithdrawalCageComponent },
  { path: "withdrawalreverse", component: WithdrawalReverseComponent },
  { path: "withdrawalreverseconfirm", component: WithdrawalReverseConfirmComponent },
  { path: "withdrawalpaywithmybank", component: WithdrawalPayWithMyBankComponent },
  { path: "transactionhistory", component: TransactionListComponent },
  { path: "limitshistory", component: LimitsHistoryComponent },
  { path: "strongauthentication", component: StrongAuthenticationComponent },
  { path: "gamesettings", component: GameSettingsComponent },
  { path: "withdrawalmail", component: WithdrawalMailComponent },
  { path: "winloss", component: WinLossComponent },
  { path: "cooloff", component: CoolOffComponent },
  { path: "accountsummary", component: AccountSummaryComponent },
  { path: "selfexclude", component: SelfExcludeComponent },
  { path: "gametransactions", component: GameTransactionsComponent },
  { path: "idologykba", component: IdologyKbaComponent },
  { path: "invalidkyc", component: InvalidKycComponent },
  { path: "cams", component: CamsComponent },
  { path: "idnow", component: IdNowComponent },
  { path: "updatephonenumber", component: UpdatePhoneNumberComponent },
  { path: "gamehistory", component: GameHistoryComponent },
  { path: "deleteaccount", component: DeleteAccountComponent },
  { path: "smslogin", component: SmsLoginComponent },
  { path: "managenotifications", component: ManageNotificationsComponent },
  { path: "spendlimit", component: SpendLimitComponent },
  { path: "mytransactionmethods", component: MyTransactionMethodsComponent },
  { path: "depositpaywithcash", component: DepositPayWithCashComponent },
  { path: "updatetoc", component: UpdateTocComponent },
  { path: "updateprivacypolicy", component: UpdatePrivacyPolicyComponent },
  { path: "updatehouserules", component: UpdateHouseRulesComponent },
  { path: "resetpassword", component: ResetPasswordComponent },
  { path: "coolingoff", component: CoolingOffComponent },
  { path: "resetpasswordverifyfirststep", component: ResetPasswordVerifyFirstStepComponent },
  { path: "resetpasswordverifysecondstep", component: ResetPasswordVerifySecondStepComponent },
  { path: "resetpasswordverifythirdstep", component: ResetPasswordVerifyThirdStepComponent },
  { path: "externallinkcontent", component: ExternalLinkContentComponent },
  { path: "passwordreset", component: PasswordResetComponent },
  { path: "withdrawalvenmo", component: WithdrawalVenmoComponent },
  { path: "playermigration", component: PlayerMigrationActionComponent },
  { path: "playermigrationaction", component: PlayerMigrationActionComponent },
  { path: "limitconfirmation", component: LimitConfirmationComponent },
  { path: "boydredeemrewards", component: BoydRedeemRewardsComponent },
  { path: "w2gpopover", component: W2GPopoverComponent},
  { path: "depositapplepay", component: DepositApplepayComponent}
];

//Add modal component routes 
const ADD_MODAL_COMPONENT_ROUTES = [];
for (let index = 0; index < COMMON_MODAL_COMPONENT_ROUTES.length; index++) {
  ADD_MODAL_COMPONENT_ROUTES.push(COMMON_MODAL_COMPONENT_ROUTES[index]);
}

const NJ_ROUTES = [
  {
    path: "casino/:name",
    loadChildren: () =>
      import("./tabs/tab-casino/tab-casino.module").then(
        (m) => m.TabCasinoPageModule
      ),
  },
  {
    path: "casino",
    loadChildren: () =>
      import("./tabs/tab-casino/tab-casino.module").then(
        (m) => m.TabCasinoPageModule
      ),
  },
  {
    path: "livedealer",
    loadChildren: () =>
      import("./tabs/tab-livedealer/tab-livedealer.module").then(
        (m) => m.TabLiveDealerPageModule
      ),
  },
  {
    path: "launcher",
    loadChildren: () =>
      import("./tabs/tab-game/tab-game.module").then(
        (m) => m.TabGamePageModule
      ),
  },
  {
    path: "promotions",
    loadChildren: () =>
      import("./tabs/tab-promotions/tab-promotions.module").then(
        (m) => m.TabPromotionsPageModule
      ),
  },
  {
    path: "c/:name",
    loadChildren: () =>
      import("./tabs/tab-webview/tab-webview.module").then(
        (m) => m.TabWebviewPageModule
      ),
  },
  {
    path: "game/:name",
    loadChildren: () =>
      import("./tabs/tab-webview/tab-webview.module").then(
        (m) => m.TabWebviewPageModule
      ),
  },
  {
    path: "c",
    redirectTo: "c",
    pathMatch: "prefix",
  },
  {
    path: "**",
    redirectTo: "casino",
    pathMatch: "full",
  },
];

const PA_ROUTES = [
  {
    path: "casino/valley-forge/:name",
    loadChildren: () =>
      import("./tabs/tab-casino/tab-casino.module").then(
        (m) => m.TabCasinoPageModule
      ),
  },
  {
    path: "casino/valley-forge",
    loadChildren: () =>
      import("./tabs/tab-casino/tab-casino.module").then(
        (m) => m.TabCasinoPageModule
      ),
  },
  {
    path: "livedealer",
    loadChildren: () =>
      import("./tabs/tab-livedealer/tab-livedealer.module").then(
        (m) => m.TabLiveDealerPageModule
      ),
  },
  {
    path: "launcher",
    loadChildren: () =>
      import("./tabs/tab-game/tab-game.module").then(
        (m) => m.TabGamePageModule
      ),
  },
  {
    path: "promotions",
    loadChildren: () =>
      import("./tabs/tab-promotions/tab-promotions.module").then(
        (m) => m.TabPromotionsPageModule
      ),
  },
  {
    path: "c/:name",
    loadChildren: () =>
      import("./tabs/tab-webview/tab-webview.module").then(
        (m) => m.TabWebviewPageModule
      ),
  },
  {
    path: "game/:name",
    loadChildren: () =>
      import("./tabs/tab-webview/tab-webview.module").then(
        (m) => m.TabWebviewPageModule
      ),
  },
  {
    path: "c",
    redirectTo: "c",
    pathMatch: "prefix",
  },
  {
    path: "**",
    redirectTo: "casino/valley-forge",
    pathMatch: "full",
  },
];

const SPLASH_ROUTES = [
  {
    path: "",
    loadChildren: () =>
      import("./tabs/tab-landing/tab-landing.module").then(
        (m) => m.TabLandingPageModule
      ),
  },
  {
    path: "**",
    redirectTo: "",
    pathMatch: "full",
  },
];

const MOBILE_ROUTES = [
  {
    path: "casino/valley-forge",
    loadChildren: () =>
      import("./tabs/tab-casino/tab-casino.module").then(
        (m) => m.TabCasinoPageModule
      ),
  },
  {
    path: "livedealer",
    loadChildren: () =>
      import("./tabs/tab-livedealer/tab-livedealer.module").then(
        (m) => m.TabLiveDealerPageModule
      ),
  },
  {
    path: "launcher",
    loadChildren: () =>
      import("./tabs/tab-game/tab-game.module").then(
        (m) => m.TabGamePageModule
      ),
  },
  {
    path: "c/:name",
    loadChildren: () =>
      import("./tabs/tab-webview/tab-webview.module").then(
        (m) => m.TabWebviewPageModule
      ),
  },
  {
    path: "casino",
    loadChildren: () =>
      import("./tabs/tab-casino/tab-casino.module").then(
        (m) => m.TabCasinoPageModule
      ),
  },
  {
    path: "casino/:name",
    loadChildren: () =>
      import("./tabs/tab-casino/tab-casino.module").then(
        (m) => m.TabCasinoPageModule
      ),
  },
  {
    path: "landing",
    loadChildren: () =>
      import("./tabs/tab-landing/tab-landing.module").then(
        (m) => m.TabLandingPageModule
      ),
  },
  {
    path: "promotions",
    loadChildren: () =>
      import("./tabs/tab-promotions/tab-promotions.module").then(
        (m) => m.TabPromotionsPageModule
      ),
  },
  {
    path: "",
    loadChildren: () =>
      import("./tabs/tab-landing/tab-landing.module").then(
        (m) => m.TabLandingPageModule
      ),
  },
  {
    path: "c",
    redirectTo: "c",
    pathMatch: "prefix",
  },
  {
    path: "**",
    redirectTo: "",
    pathMatch: "full",
  },
];



export function init_app(
  injector: Injector,
  config: ConfigService,
  appstorageTsService: AppstorageTsService,
  marketingService: MarketingService,
  platform: Platform
) {
  return () =>
    new Promise((resolve) => {
      let routes = [];
      if (platform.is("capacitor")) {
        config.isLoaded().then(() => {
          //routes = MOBILE_ROUTES;
          const ALL_MOBILE_ROUTES = [...COMMON_MODAL_COMPONENT_ROUTES, ...MOBILE_ROUTES]
          routes = ALL_MOBILE_ROUTES;
          const router: Router = injector.get(Router);
          router.resetConfig(routes);
          resolve(this);
        })

      } else {
        config.isLoaded().then(() => {

          switch (config.getDeafaultRoute()) {
            case "/":
              routes = SPLASH_ROUTES;
              break;
            case "casino":
              //routes = NJ_ROUTES;
              const ALL_NJ_ROUTES = [...COMMON_MODAL_COMPONENT_ROUTES, ...NJ_ROUTES]
              routes = ALL_NJ_ROUTES;
              break;
            case "casino/valley-forge":
              //routes = PA_ROUTES;
              const ALL_PA_ROUTES = [...COMMON_MODAL_COMPONENT_ROUTES, ...PA_ROUTES]
              routes = ALL_PA_ROUTES;
              break;
            default:
              routes = SPLASH_ROUTES;
              break;
          }
          const router: Router = injector.get(Router);
          router.resetConfig(routes);
          resolve(this);
        });
      }
    });
}

const ionicRootOptions = {
  rippleEffect: false,
  navAnimation: customTabAnimation
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    IonicModule.forRoot(ionicRootOptions),
    IonicStorageModule.forRoot(),
    AppRoutingModule,
    ReactiveFormsModule,
    HttpClientModule,
    ServicesSharedModule,
    ComponentsModule,
    CommonModule,
    FormsModule,
    pagesModule,
    NgIdleModule.forRoot(),
    TabLandingPageModule,
  ],

  providers: [
    ConfigService,
    AppstorageTsService,
    MarketingService,
    Platform,
    {
      provide: APP_INITIALIZER,
      useFactory: init_app,
      deps: [Injector, ConfigService, AppstorageTsService, MarketingService, Platform],
      multi: true,
    },
    {
      provide: RouteReuseStrategy,
      useClass: IonicRouteStrategy,
    },
    ,
    AuthenticationService,
    GlobalErrorHandlerService,
    LogoutService,
    NotificationApiService,
    UserSessionService,
    AuthGuardService,
    InAppBrowser,
    TouchID,
    Device,
    DatePipe,
    CommonModule,
    NavParams,
    ScreenOrientation,
    ApplePay,
    FileOpener
  ],

  bootstrap: [AppComponent],
})
export class AppModule { }
